'use client';

import { useState } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  LuHouse,
  LuKey,
  LuClipboardList,
  LuBuilding,
  LuMail,
  LuPhone,
  LuCalendar,
  LuLoader,
  LuCheck,
  LuArrowRight,
  LuX,
} from 'react-icons/lu';
import { toast, Toaster } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { useServiceForm } from '@/hooks/useServiceForm';
import { ServiceType } from '@prisma/client';
import { useUser } from '@clerk/nextjs';

const ServiceSchema = Yup.object().shape({
  name: Yup.string().required('Required'),
  email: Yup.string().email('Invalid email').required('Required'),
  phone: Yup.string().nullable(),
  serviceType: Yup.string().required('Required'),
  propertyType: Yup.string().required('Required'),
  propertyAddress: Yup.string().required('Required'),
  message: Yup.string(),
  preferredDate: Yup.date()
    .required('Required')
    .min(new Date(), 'Date cannot be in the past')
    .test(
      'is-weekday',
      'Please select a weekday (Monday to Friday)',
      function (value) {
        if (!value) return true; // Skip validation if no date provided
        const day = new Date(value).getDay();
        return day !== 0 && day !== 6; // 0 is Sunday, 6 is Saturday
      }
    ),
});

const PropertyManagementPage = () => {
  const { user, isSignedIn } = useUser();
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [activeService, setActiveService] = useState<number | null>(null);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(
    null
  );
  const { isSubmitting, submitServiceRequest } = useServiceForm();

  const services = [
    {
      icon: LuHouse,
      title: 'Property Maintenance',
      description: 'Comprehensive property maintenance and management services',
      features: [
        'Regular inspections',
        'Preventive maintenance',
        'Emergency repairs',
        'Quality assurance',
      ],
      image: '/images/pages/property-maintenance.jpg',
      overlayColor: 'from-purple-600/80',
    },
    {
      icon: LuKey,
      title: 'Tenant Management',
      description: 'Complete tenant management and relationship services',
      features: [
        'Tenant screening',
        'Lease management',
        'Rent collection',
        'Tenant relations',
      ],
      image: '/images/pages/property-tenant-management.jpg',
      overlayColor: 'from-violet-600/80',
    },
    {
      icon: LuBuilding,
      title: 'Villa Management',
      description: 'Specialized management services for luxury properties',
      features: [
        'Security management',
        'Staff supervision',
        'Amenity maintenance',
        'Guest services',
      ],
      image: '/images/pages/property-villa-management.jpg',
      overlayColor: 'from-fuchsia-600/80',
    },
    {
      icon: LuClipboardList,
      title: 'Home Services',
      description: 'Comprehensive home services and delivery solutions',
      features: [
        'Scheduled deliveries',
        'Moving assistance',
        'Setup services',
        'Quality control',
      ],
      image: '/images/pages/property-home-movers.jpg',
      overlayColor: 'from-indigo-600/80',
    },
  ];

  const propertyTypes = [
    'Residential Apartment',
    'Commercial Space',
    'Villa/Luxury Home',
    'Mixed-Use Property',
    'Retail Space',
    'Office Building',
  ];

  // Define an interface for the form values
  interface FormValues {
    name: string;
    email: string;
    phone: string;
    serviceType: string;
    propertyType: string;
    propertyAddress: string;
    message: string;
    preferredDate: string;
  }

  const handleSubmit = async (
    values: FormValues,
    { resetForm }: { resetForm: () => void }
  ) => {
    // Set initial status
    setSubmitStatus(null);

    try {
      // Prepare the data for our service API
      const serviceData = {
        name: values.name,
        email: values.email,
        phone: values.phone || '',
        message: values.message,
        serviceType: ServiceType.PROPERTY_MANAGEMENT,
        preferredDate: new Date(values.preferredDate),
        location: values.propertyAddress,
        propertyType: values.propertyType,
        specificService: values.serviceType,
      };

      // Submit to service API using the hook (which handles toast notifications)
      const result = await submitServiceRequest(serviceData);

      if (result.success) {
        setSubmitStatus('success');
        resetForm();
        setSelectedService(null);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    }
  };

  return (
    <div className='min-h-screen bg-[#130c1a]'>
      <Toaster position='top-center' />
      {/* Hero Section */}
      <div className='relative h-[40vh] md:h-[60vh] overflow-hidden'>
        <div className='absolute inset-0'>
          <img
            src='/images/pages/property-hero.jpg'
            alt='Property Management Services'
            className='h-full w-full object-cover'
          />
          <div className='absolute inset-0 bg-linear-to-b from-[#130c1a]/50 via-[#130c1a]/80 to-[#130c1a]' />
        </div>
        <div className='relative h-full mx-auto max-w-7xl px-4'>
          <div className='flex h-full flex-col items-center justify-center text-center'>
            <h1 className='text-3xl md:text-4xl lg:text-6xl font-bold text-white'>
              Property Management Solutions
            </h1>
            <p className='mt-3 md:mt-6 text-base md:text-xl text-purple-100'>
              Professional property management services for your real estate
              investments
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className='relative mx-auto max-w-7xl px-4 py-8 md:py-16'>
        {/* Contact Info Cards */}
        <div className='grid gap-3 md:gap-4 grid-cols-1 sm:grid-cols-3 mb-8 md:mb-16'>
          {[
            { icon: LuPhone, label: 'Phone', value: '+254 756 211 546' },
            { icon: LuMail, label: 'Email', value: 'property@qwetulands.com' },
            {
              icon: LuCalendar,
              label: 'Hours',
              value: '24/7 Support Available',
            },
          ].map((contact, index) => (
            <div
              key={index}
              className='group relative overflow-hidden rounded-xl bg-purple-950/30 backdrop-blur-xs p-3 md:p-4 transition-all duration-300 hover:-translate-y-1'
            >
              <div className='flex items-center gap-3 md:gap-4'>
                <div className='rounded-xl bg-purple-500/20 p-2 md:p-3 text-purple-400 transition-transform duration-300 group-hover:scale-110'>
                  <contact.icon className='h-5 w-5 md:h-6 md:w-6' />
                </div>
                <div>
                  <p className='text-sm md:text-base text-purple-300/70'>
                    {contact.label}
                  </p>
                  <p className='text-sm md:text-base font-medium text-purple-100'>
                    {contact.value}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Services Grid */}
        <div className='grid gap-4 md:gap-6 grid-cols-1 md:grid-cols-2 mb-8 md:mb-16'>
          {services.map((service, idx) => (
            <div
              key={idx}
              className='group relative isolate cursor-pointer'
              onMouseEnter={() => setActiveService(idx)}
              onMouseLeave={() => setActiveService(null)}
              onClick={() => setSelectedService(service.title)}
            >
              <div className='relative h-[250px] md:h-[400px] overflow-hidden rounded-2xl md:rounded-3xl transition-all duration-500'>
                <img
                  src={service.image}
                  alt={service.title}
                  className='absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-110'
                />

                <div
                  className={`absolute inset-0 bg-linear-to-b ${
                    service.overlayColor
                  } to-[#130c1a]/90 transition-opacity duration-500
                  ${activeService === idx ? 'opacity-90' : 'opacity-80'}`}
                />

                <div className='relative h-full p-4 md:p-8 flex flex-col justify-end'>
                  <div className='mb-3 md:mb-6'>
                    <h3
                      className='text-xl md:text-2xl font-bold text-white mb-2 md:mb-4 transform transition-transform duration-500
                      group-hover:-translate-y-2'
                    >
                      {service.title}
                    </h3>
                    <p
                      className='text-sm md:text-base text-gray-200 transform transition-all duration-500 
                      group-hover:-translate-y-2 opacity-80 group-hover:opacity-100'
                    >
                      {service.description}
                    </p>
                  </div>

                  <ul
                    className='space-y-1 md:space-y-2 transform transition-all duration-500 
                    translate-y-8 opacity-0 group-hover:translate-y-0 group-hover:opacity-100'
                  >
                    {service.features.map((feature, i) => (
                      <li key={i} className='flex items-center space-x-2'>
                        <span className='shrink-0 rounded-full bg-purple-500/20 p-1'>
                          <LuCheck className='h-3 w-3 md:h-4 md:w-4 text-purple-300' />
                        </span>
                        <span className='text-xs md:text-sm text-purple-100'>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Form Section - Conditionally Rendered */}
        {selectedService ? (
          <Formik
            initialValues={{
              name: isSignedIn && user?.fullName ? user.fullName : '',
              email:
                isSignedIn && user?.primaryEmailAddress?.emailAddress
                  ? user.primaryEmailAddress.emailAddress
                  : '',
              phone: '',
              serviceType: selectedService || '',
              propertyType: '',
              propertyAddress: '',
              message: '',
              preferredDate: new Date().toISOString().split('T')[0],
            }}
            validationSchema={ServiceSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ errors, touched, isValid, setFieldValue }) => (
              <>
                <Form className='space-y-4 md:space-y-6'>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6'>
                    <div>
                      <label
                        htmlFor='name'
                        className='mb-1.5 block text-sm font-medium text-purple-200'
                      >
                        Full Name
                      </label>
                      <Field
                        name='name'
                        className='w-full rounded-lg border border-purple-600/50 bg-[#1c0f2e]/60 p-3 text-sm text-purple-50 placeholder-purple-400/60 focus:border-purple-500 focus:ring-3 focus:ring-purple-500/30 transition'
                        placeholder='Your Full Name'
                        readOnly={isSignedIn}
                      />
                      {errors.name && touched.name ? (
                        <div className='mt-1.5 text-xs text-red-400'>
                          {errors.name}
                        </div>
                      ) : null}
                    </div>
                    <div>
                      <label
                        htmlFor='email'
                        className='mb-1.5 block text-sm font-medium text-purple-200'
                      >
                        Email Address
                      </label>
                      <Field
                        name='email'
                        type='email'
                        className='w-full rounded-lg border border-purple-600/50 bg-[#1c0f2e]/60 p-3 text-sm text-purple-50 placeholder-purple-400/60 focus:border-purple-500 focus:ring-3 focus:ring-purple-500/30 transition'
                        placeholder='your.email@example.com'
                        readOnly={isSignedIn}
                      />
                      {errors.email && touched.email ? (
                        <div className='mt-1.5 text-xs text-red-400'>
                          {errors.email}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className='md:col-span-2'>
                    <label
                      htmlFor='phone'
                      className='mb-1 md:mb-2 block text-xs md:text-sm font-medium text-purple-300'
                    >
                      Phone Number
                    </label>
                    <Field
                      name='phone'
                      className='service-purple-input'
                      placeholder='+254 7XX XXX XXX'
                    />
                    {errors.phone && touched.phone && (
                      <div className='mt-1 text-xs md:text-sm text-red-400'>
                        {errors.phone}
                      </div>
                    )}
                  </div>

                  <div className='md:col-span-2'>
                    <label
                      htmlFor='serviceType'
                      className='mb-1 md:mb-2 block text-xs md:text-sm font-medium text-purple-300'
                    >
                      Service Type
                    </label>
                    <Field
                      as='select'
                      name='serviceType'
                      className='service-purple-input'
                    >
                      {services.map((service) => (
                        <option
                          key={service.title}
                          value={service.title}
                          className='bg-purple-900'
                        >
                          {service.title}
                        </option>
                      ))}
                    </Field>
                  </div>

                  <div>
                    <label
                      htmlFor='propertyType'
                      className='mb-1 md:mb-2 block text-xs md:text-sm font-medium text-purple-300'
                    >
                      Property Type
                    </label>
                    <Field
                      as='select'
                      name='propertyType'
                      className='service-purple-input'
                    >
                      <option value='' className='bg-purple-900'>
                        Select Property Type
                      </option>
                      {propertyTypes.map((type) => (
                        <option
                          key={type}
                          value={type}
                          className='bg-purple-900'
                        >
                          {type}
                        </option>
                      ))}
                    </Field>
                    {errors.propertyType && touched.propertyType && (
                      <div className='mt-1 text-xs md:text-sm text-red-400'>
                        {errors.propertyType}
                      </div>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor='propertyAddress'
                      className='mb-1 md:mb-2 block text-xs md:text-sm font-medium text-purple-300'
                    >
                      Property Address
                    </label>
                    <Field
                      name='propertyAddress'
                      className='service-purple-input'
                      placeholder='e.g. 123 Main St, Nairobi'
                    />
                    {errors.propertyAddress && touched.propertyAddress && (
                      <div className='mt-1 text-xs md:text-sm text-red-400'>
                        {errors.propertyAddress}
                      </div>
                    )}
                  </div>

                  <div className='md:col-span-2'>
                    <label
                      htmlFor='preferredDate'
                      className='mb-1 md:mb-2 block text-xs md:text-sm font-medium text-purple-300'
                    >
                      Preferred Date
                    </label>
                    <Field
                      name='preferredDate'
                      type='date'
                      className='service-purple-input'
                      min={new Date().toISOString().split('T')[0]}
                    />
                    {errors.preferredDate && touched.preferredDate && (
                      <div className='mt-1 text-xs md:text-sm text-red-400'>
                        {errors.preferredDate}
                      </div>
                    )}
                    <div className='mt-1 text-xs text-purple-300'>
                      Service booking can only be effected on weekdays
                      (Monday-Friday)
                    </div>
                  </div>

                  <div className='md:col-span-2'>
                    <label
                      htmlFor='message'
                      className='mb-1 md:mb-2 block text-xs md:text-sm font-medium text-purple-300'
                    >
                      Additional Information
                    </label>
                    <Field
                      as='textarea'
                      name='message'
                      rows={3}
                      className='service-purple-input'
                      placeholder='Any specific requirements or questions...'
                    />
                  </div>

                  <div className='flex justify-end mt-6 md:col-span-2'>
                    <button
                      type='button'
                      onClick={() => setSelectedService(null)}
                      className='mr-3 md:mr-4 rounded-lg border border-purple-500 px-4 md:px-6 py-2 md:py-3 text-xs md:text-sm text-purple-300 transition-colors hover:bg-purple-900 hover:text-purple-300'
                    >
                      Cancel
                    </button>
                    <button
                      type='submit'
                      disabled={isSubmitting || !isValid}
                      className='flex items-center justify-center space-x-1 md:space-x-2 rounded-md xs:rounded-lg bg-linear-to-r from-purple-600 to-violet-600 px-3 xs:px-3.5 sm:px-4 md:px-6 py-1.5 xs:py-2 sm:py-2 md:py-3 text-[11px] xs:text-xs md:text-sm text-white transition-all hover:from-purple-500 hover:to-violet-500 disabled:opacity-70 hover:shadow-lg hover:shadow-purple-500/20 hover:scale-105 min-w-[100px] xs:min-w-[120px] sm:min-w-[140px]'
                    >
                      {isSubmitting ? (
                        <>
                          <LuLoader className='h-4 w-4 md:h-5 md:w-5 animate-spin' />
                          <span>Submitting...</span>
                        </>
                      ) : (
                        <>
                          <span>Submit Request</span>
                          <LuArrowRight className='h-4 w-4 md:h-5 md:w-5' />
                        </>
                      )}
                    </button>
                  </div>
                </Form>
              </>
            )}
          </Formik>
        ) : (
          <div className='text-center'>
            <p className='mb-4 md:mb-6 text-sm md:text-base text-purple-300'>
              Please select a service from the options above to request a quote
              or consultation
            </p>
            <div className='grid gap-2 md:gap-4 grid-cols-2 sm:grid-cols-2 md:grid-cols-4'>
              {services.map((service, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedService(service.title)}
                  className='group flex flex-col items-center rounded-xl bg-purple-900/30 p-3 md:p-4 transition-all duration-300 hover:bg-purple-800/40 hover:shadow-lg hover:shadow-purple-500/10 hover:scale-105 border border-purple-800/10 hover:border-purple-700/30'
                >
                  <div className='mb-2 md:mb-3 rounded-full bg-purple-800/70 p-2 md:p-3 text-purple-300 transition-transform duration-300 group-hover:scale-110 group-hover:text-purple-300'>
                    <service.icon className='h-4 w-4 md:h-6 md:w-6' />
                  </div>
                  <span className='text-xs md:text-sm font-medium text-white'>
                    {service.title}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertyManagementPage;
